import { ThemeConfig } from "@cede/utils";
import { HtmlComponent, UIComponent, UIConfig } from "../types/component";
import { globalStyle } from "./globalStyle";

export class UI {
  // Shadow dom to isolate styles
  private shadow: ShadowRoot;

  // Layout to contain components
  private layout: HTMLElement;

  // Keep track of active components to remove them later
  private activeComponents: UIComponent[] = [];

  constructor(private root: HTMLElement, private theme: ThemeConfig, private config: UIConfig) {
    // Init shadow wrapper to contain shadow dom
    const shadowWrapper = document.createElement("div");
    this.root.appendChild(shadowWrapper);
    this.shadow = shadowWrapper.attachShadow({ mode: "open" });

    // Setup global style
    const globalStyleElement = document.createElement("style");
    globalStyleElement.innerHTML = globalStyle(this.theme);
    globalStyleElement.id = "cede-global-style";
    this.shadow.appendChild(globalStyleElement);

    // Init layout to contain components
    this.layout = document.createElement("div");
    // we don't display the widget layout if we need to display only the installation modal
    if (config?.displayLayout !== false) this.layout.classList.add("cede-layout");
    this.layout.id = "cede-layout";
    this.shadow.appendChild(this.layout);
  }

  renderHtml(
    component: HtmlComponent,
    { clearPrevious = true, animateOnExit = false, layout = false, data = {} } = {},
  ) {
    // Clear previous components
    if (this.activeComponents?.length && clearPrevious && !layout) {
      this.activeComponents.forEach((component) => this.remove(component));
      this.activeComponents = [];
    }

    // Setup component style
    const style = document.createElement("style");
    style.innerHTML = component.style(this.theme);
    this.shadow.appendChild(style);
    if (component.name) {
      style.id = `${component.name}-style`;
    }

    // Setup component html
    const html = document.createElement("div");
    html.classList.add("component-container");
    html.innerHTML = component.html(this.config, data);
    this.layout.appendChild(html);
    if (component.name) {
      html.id = `${component.name}-html`;
    }

    const newActiveComponent: UIComponent = { style, html, opts: { animateOnExit } };
    // We don't want to clear layout components when we add a new one
    if (!layout) {
      this.activeComponents.push(newActiveComponent);
    }

    if (component.action) {
      component.action(newActiveComponent, this);
    }
  }

  remove(component: UIComponent) {
    if (component.opts?.animateOnExit) {
      // We need to let the animation finish before removing the component
      component.html.classList.add("animate-exit");
      setTimeout(() => {
        this.layout.removeChild(component.html);
        this.shadow.removeChild(component.style);
      }, 600);
      return;
    }

    this.layout.removeChild(component.html);
    this.shadow.removeChild(component.style);
  }

  clear() {
    this.root.innerHTML = "";
  }
}
